import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import lscache from "lscache";
import { ChangeEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LazyLoad from "react-lazyload";
import { toast } from "react-toastify";
import { useUser } from "../../contexts/UserContext";
import { useTravelerFunctions } from "../../hooks/travelers/useTravelerFunctions";
import travelerService from "../../services/traveler-service";
import {
  default as ITraveler,
  TravelerComponentProps,
  default as TravelerContent,
} from "../../types/traveler-type";
import CalendarSelect from "../Forms/CalendarSelect";
import GroupedCountries from "../Handlers/CountryHandler";

const TravelerComponent: React.FC<TravelerComponentProps> = ({ canDelete }) => {
  const [showForm, setShowForm] = useState<boolean | null>(null);

  const initialFormData = {
    title: "",
    content: "",
    city: "",
    country: "Finland",
    travelStartDate: dayjs(),
    travelEndDate: dayjs(),
  };

  const intl = useIntl();

  const [formData, setFormData] = useState<ITraveler>(initialFormData);

  const { userId } = useUser();

  const { travelers, isLoading, fetchTravelers, handleTravelerDelete } =
    useTravelerFunctions();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("apartment-" + name, value, 30);
  };

  const handleDateChange = (date: dayjs.Dayjs, name: string) => {
    if (date) {
      setFormData({ ...formData, [name]: date });
    }
  };

  const handleTravelerSubmit = async () => {
    try {
      const newTraveler: TravelerContent = {
        title: formData.title,
        content: formData.content,
        city: formData.city,
        country: formData.country,
        travelStartDate: formData.travelStartDate,
        travelEndDate: formData.travelEndDate,
        creatorId: userId,
      };

      await travelerService.createTraveler(newTraveler);
      toast.success("Success!", {
        position: "bottom-right",
      });
      setFormData(initialFormData);
      setShowForm(false);
      fetchTravelers();
    } catch (error) {
      toast.error("Error publishing traveler", {
        position: "bottom-right",
      });
    }
  };

  return (
    <Container>
      <Grid container>
        <Grid item xs={12} sx={{ my: 2 }}>
          <Typography variant="h2">
            <FormattedMessage id="travelers" />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            sx={{ mt: 2, mb: 2, width: { xs: "100%", sm: "90%" } }}
            variant="contained"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm === null && (
              <Typography variant="body1">
                <FormattedMessage id="create-traveler" />
              </Typography>
            )}
            {showForm !== null && (
              <Typography variant="body1">
                {showForm ? (
                  <FormattedMessage id="hide" />
                ) : (
                  <FormattedMessage id="show" />
                )}
              </Typography>
            )}
          </Button>
        </Grid>
        {isLoading && <CircularProgress />}
        {showForm && (
          <Box sx={{ my: 2 }}>
            <Grid item xs={12}>
              <Typography variant="h3">
                <FormattedMessage id="create-traveler" />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="title"
                margin="normal"
                variant="outlined"
                label={<FormattedMessage id="title" />}
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="content"
                margin="normal"
                variant="outlined"
                label={<FormattedMessage id="content" />}
                value={formData.content}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <GroupedCountries
                country={formData.country!}
                handleCountryChange={(event, newValue) =>
                  setFormData({ ...formData, country: newValue! })
                }
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                label={intl.formatMessage({
                  id: "city",
                  defaultMessage: "City",
                })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CalendarSelect
                start={dayjs(formData.travelStartDate)}
                handleDateChange={(date) =>
                  handleDateChange(date, "travelStartDate")
                }
                name="travelStartDate"
                id="start-of-trip"
                message="Trip start"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CalendarSelect
                start={dayjs(formData.travelEndDate)}
                handleDateChange={(date) =>
                  handleDateChange(date, "travelEndDate")
                }
                name="travelEndDate"
                id="end-of-trip"
                message="Trip end"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTravelerSubmit}
              >
                <FormattedMessage id="submit" />
              </Button>
            </Grid>
          </Box>
        )}
        <>
          {travelers.map((traveler: TravelerContent) => (
            <LazyLoad key={traveler.id} style={{ width: "100%" }}>
              <Grid item xs={12} p={2}>
                <Card sx={{ my: 2, p: 2 }}>
                  <Typography variant="h3">{traveler.title}</Typography>
                  <Typography variant="body1">{traveler.content}</Typography>
                  <Typography variant="body1">{traveler.city}</Typography>
                  <Typography variant="body1">{traveler.country}</Typography>
                  <Typography variant="body2">
                    {dayjs(traveler.travelStartDate).format("MMMM D, YYYY")}
                  </Typography>
                  <Typography variant="body2">
                    {dayjs(traveler.travelEndDate).format("MMMM D, YYYY")}
                  </Typography>
                  {(canDelete || traveler.creatorId === userId) && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        onClick={() => handleTravelerDelete(traveler)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Card>
              </Grid>
            </LazyLoad>
          ))}
        </>
      </Grid>
    </Container>
  );
};

export default TravelerComponent;
